import { useCallback } from "react";

import { generateId } from "~/utils/helpers";
import { useRerender } from "~/hooks/useRerender";

import type { ToastProps } from "./Toast";

export interface ToastConfig
  extends Pick<
    ToastProps,
    | "description"
    | "dismissable"
    | "icon"
    | "iconVariant"
    | "id"
    | "intent"
    | "onDismiss"
    | "title"
  > {
  autoClose?: boolean | number;
  actionText?: string;
  onActionClick?: () => void;
}

const DEFAULT_DURATION = 5000;

const ToastStore = new Map<string, ToastConfig>();

/**
 * Hook for managing toasts
 */
export const useToasts = () => {
  const rerender = useRerender();

  const dismissToast = useCallback(
    (id: string) => {
      ToastStore.get(id)?.onDismiss?.(id);
      ToastStore.delete(id);
      rerender();
    },
    [rerender],
  );

  const dismissAllToasts = useCallback(() => {
    ToastStore.forEach(({ id, onDismiss }) => {
      onDismiss?.(id!);
    });
    ToastStore.clear();
    rerender();
  }, [rerender]);

  const showToast = useCallback(
    (config: ToastConfig) => {
      const configWithDefaults = {
        ...config,
        id: config.id ?? generateId(),
        autoClose: config.autoClose ?? true,
      };

      ToastStore.set(configWithDefaults.id, configWithDefaults);

      if (configWithDefaults.autoClose) {
        setTimeout(
          () => {
            dismissToast(configWithDefaults.id);
          },
          configWithDefaults.autoClose === true
            ? DEFAULT_DURATION
            : configWithDefaults.autoClose,
        );
      }

      rerender();

      return configWithDefaults.id;
    },
    [dismissToast, rerender],
  );

  return {
    toasts: Array.from(ToastStore.values()),
    showToast,
    dismissToast,
    dismissAllToasts,
  };
};
